/**
 * React Query hook for fetching Gmail connection status.
 */

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';
import { fetchWithDefaults } from '@/lib/http/client';

import type { ConnectionStatus } from './connectionStatus';

export interface GmailStatusAccount {
  email: string | null;
  social_account_id: number | null;
  social_account_org_id: string;
  gmail_entity_id: string;
  gcal_entity_id: string;
  is_primary: boolean;
  needs_reconnect?: boolean;
  gmail_default_data_visibility?: 'private' | 'org';
  gcal_default_data_visibility?: 'private' | 'org';
  /**
   * Set to true when ahoy_default_data_visibility was auto-flipped to 'private'
   * during the most recent disconnect (only when it was 'org' beforehand). The
   * UI uses this to surface a reconnect prompt asking the user whether they
   * want to share data with their team again. Cleared once the user acts on
   * the prompt or manually changes visibility.
   */
  gmail_visibility_flipped_on_disconnect?: boolean;
  gcal_visibility_flipped_on_disconnect?: boolean;
  email_alias_filter?: string;
  connection_status?: ConnectionStatus;
  missing_scopes?: string[];
}

export interface GmailStatus {
  connected: boolean;
  needs_reconnect: boolean;
  email?: string | null;
  accounts?: GmailStatusAccount[];
  // Backward compatibility for single-account responses
  social_account_id?: number | null;
}

interface UseGmailStatusOptions {
  enabled?: boolean;
}

async function fetchGmailStatus(orgId?: string): Promise<GmailStatus> {
  const url = orgId
    ? `/users/api/gmail/status/?org_id=${orgId}`
    : '/users/api/gmail/status/';

  const response = await fetchWithDefaults(url);

  if (!response.ok) {
    throw new ApiError(
      `Gmail status failed: ${response.status}`,
      response.status
    );
  }

  return response.json();
}

export function useGmailStatus(
  orgId?: string,
  options?: UseGmailStatusOptions
) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['google', 'gmail', 'status', orgId],
    queryFn: () => fetchGmailStatus(orgId),
    staleTime: 5 * 60_000, // 5 minutes - mutations invalidate cache
    gcTime: 10 * 60_000, // 10 minutes
    enabled: options?.enabled ?? true,
  });

  return { data, isLoading, error, refetch };
}
